import styled from 'styled-components';
import { Box, Flex } from '@rebass/grid';
import Img, { BaseImgProps } from './Img';
import React, { FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import Accomodation from './svg/Accomodation';
import Activity from './svg/Activity';
import Company from './svg/Company';
import { FormattedMessage } from 'react-intl';
import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import { ActiveFavoriteButton, getAddFavoriteMutation, InactiveFavoriteButton } from './FavoriteButton';
import { useMutation } from '../apollo';
import {
    AddFavoriteActivityInput,
    AddFavoriteCompanyInput,
    AddFavoriteRentalUnitInput,
    AddFavoriteRentalUnitMutation,
    FavoriteButtonDocument,
    MyDocument,
    MyQuery,
    useRemoveFavoriteMutation,
} from '../generated/graphql';
import { UnreachableCaseError } from '@oberoninternal/travelbase-ds/entities/UnreachableCaseError';
import { toast } from 'react-toastify';
import Toast from '@oberoninternal/travelbase-ds/components/feedback/Toast';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Link from 'next/link';
import LinkWrapper from './LinkWrapper';
import { useApolloClient } from '@apollo/client';
import useGetPageSlugByLocale from '../hooks/useGetPageSlugByLocale';

interface Props {
    title: string;
    type: 'rentalUnit' | 'activity' | 'company';
    slug: string;
    id: string;
    favoriteId: string;
    imgProps?: BaseImgProps | null;
    showFavoriteButton?: boolean;
}

const FavoriteTile: FC<React.PropsWithChildren<Props>> = ({
    title,
    type,
    imgProps,
    id,
    showFavoriteButton,
    slug,
    favoriteId: initialFavoriteId,
}) => {
    const [addFavorite, { loading: addFavoriteLoading }] = useMutation<
        AddFavoriteRentalUnitMutation,
        { input: AddFavoriteActivityInput | AddFavoriteCompanyInput | AddFavoriteRentalUnitInput }
    >(getAddFavoriteMutation(type));
    const [removeFavorite, { loading: removeFavoriteLoading }] = useRemoveFavoriteMutation();
    const loading = addFavoriteLoading || removeFavoriteLoading;
    const [favoriteId, setFavoriteId] = useState<string | null>(initialFavoriteId);
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const accommodationPageSlug = getPageSlugByLocale('ACCOMMODATION');
    const activityPageSlug = getPageSlugByLocale('ACTIVITY');
    const companyPageSlug = getPageSlugByLocale('COMPANY');
    const client = useApolloClient();
    const onAdd = useCallback(async () => {
        try {
            const result = await addFavorite({
                variables: (() => {
                    switch (type) {
                        case 'activity':
                            return {
                                input: {
                                    activityId: id,
                                },
                            };
                        case 'company':
                            return {
                                input: {
                                    companyId: id,
                                },
                            };
                        case 'rentalUnit':
                            return {
                                input: {
                                    rentalUnitId: id,
                                },
                            };
                        default:
                            throw new UnreachableCaseError(type);
                    }
                })(),
            });
            setFavoriteId(result.data?.result.favorite?.id ?? null);
            const data = client.readQuery<MyQuery>({
                query: MyDocument,
            });
            if (result.data?.result.favorite?.id) {
                // update cache ourselves, so we don't have to make a roundtrip to the server again
                client.writeQuery<MyQuery>({
                    query: FavoriteButtonDocument,
                    broadcast: false,
                    variables: {},
                    data: {
                        __typename: 'Query',
                        viewer: {
                            __typename: 'Customer',
                            ...data!.viewer!,
                            favorites: [...data!.viewer!.favorites, result.data.result.favorite],
                        },
                    },
                });
            }
        } catch (ex) {
            // eslint-disable-next-line no-console
            console.error(ex);
            toast(
                <Toast title={<FormattedMessage defaultMessage="Favoriet toevoegen mislukt" />} variant="error">
                    <Body variant="small">
                        <FormattedMessage defaultMessage="Probeer het later opnieuw" />
                    </Body>
                </Toast>,
                { autoClose: 3000 }
            );
        }
    }, [addFavorite, client, id, type]);

    const onRemove = useCallback(async () => {
        try {
            await removeFavorite({
                variables: { input: { favoriteId: favoriteId! } },
            });
            setFavoriteId(null);
            const data = client.readQuery<MyQuery>({
                query: MyDocument,
            });
            if (data)
                client.writeQuery<MyQuery>({
                    query: MyDocument,
                    broadcast: false,
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer!,
                            favorites: data.viewer!.favorites.filter(fav => fav.id !== favoriteId),
                        },
                    },
                });
        } catch (ex) {
            // eslint-disable-next-line no-console
            console.error(ex);
            toast(
                <Toast title={<FormattedMessage defaultMessage="Favoriet verwijderen mislukt" />} variant="error">
                    <Body variant="small">
                        <FormattedMessage defaultMessage="Probeer het later opnieuw" />
                    </Body>
                </Toast>,
                { autoClose: 3000 }
            );
        }
    }, [client, favoriteId, removeFavorite]);

    const onClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (favoriteId) {
                onRemove();
            } else {
                onAdd();
            }
        },
        [favoriteId, onAdd, onRemove]
    );
    const FavoriteButton = favoriteId ? ActiveFavoriteButton : InactiveFavoriteButton;
    const href = useMemo(() => {
        switch (type) {
            case 'activity':
                return `/${activityPageSlug}/${slug}`;
            case 'company':
                return `/${companyPageSlug}/${slug}`;
            case 'rentalUnit':
            default:
                return `/${accommodationPageSlug}/${slug}`;
        }
    }, [accommodationPageSlug, activityPageSlug, companyPageSlug, slug, type]);

    return (
        <Link href={href} passHref legacyBehavior>
            <LinkWrapper>
                <Wrapper>
                    {imgProps && (
                        <ImgWrapper>
                            <Img {...imgProps} layout="fill" />
                        </ImgWrapper>
                    )}
                    <Flex
                        flex={1}
                        pl={[4, null, '24px']}
                        py={[4, null, 6]}
                        pr={[4, null, 6]}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Label>
                                {type === 'company' && (
                                    <>
                                        <Company /> <FormattedMessage defaultMessage="Bedrijf" />
                                    </>
                                )}
                                {type === 'rentalUnit' && (
                                    <>
                                        <Accomodation /> <FormattedMessage defaultMessage="Accommodatie" />
                                    </>
                                )}
                                {type === 'activity' && (
                                    <>
                                        <Activity /> <FormattedMessage defaultMessage="Activiteit" />
                                    </>
                                )}
                            </Label>
                            <Title variant="small">{title}</Title>
                        </Box>
                        {showFavoriteButton && (
                            <FavoriteButton onClick={onClick} submitting={loading} disabled={loading} />
                        )}
                    </Flex>
                </Wrapper>
            </LinkWrapper>
        </Link>
    );
};

export default FavoriteTile;

const Wrapper = styled(Flex)`
    background: ${({ theme }) => theme.colors.neutral[0]};
    border: 0.1rem solid rgba(0, 0, 0, 0.17);
    box-shadow: 0 2.4rem 1.4rem rgba(0, 0, 0, 0.01), 0px 11px 11px rgba(0, 0, 0, 0.02), 0px 3px 6px rgba(0, 0, 0, 0.03),
        0px 0px 0px rgba(0, 0, 0, 0.03);
    border-radius: 0.8rem;
    overflow: hidden;
`;

const ImgWrapper = styled.div`
    aspect-ratio: 16 / 9;
    flex: 1;
    max-width: 16.5rem;
    min-width: 16.5rem;
    position: relative;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        aspect-ratio: 1 / 1;
        max-width: 8rem;
        min-width: 8rem;
    }
`;
